import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import type { NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const ARTICLE_ID = 'sddr-hosteleria-deposito-envases-2026'
const k = (f: string) => `news.articles.${ARTICLE_ID}.${f}`

const REF = {
    boe: {
        href: 'https://www.boe.es/buscar/act.php?id=BOE-A-2022-22690',
        domain: 'boe.es',
        label: 'BOE · RD 1055/2022',
    },
    miteco: {
        href: 'https://www.miteco.gob.es/content/dam/miteco/es/calidad-y-evaluacion-ambiental/sgecocir/plasticos--sup/INFORME%20RECOGIDA%20SEPARADA%20BOTELLAS%20SUP%20A%C3%91O%202023_.pdf',
        domain: 'miteco.gob.es',
        label: 'MITECO · Informe 2023',
    },
    consulta: {
        href: 'https://www.miteco.gob.es/content/dam/miteco/es/calidad-y-evaluacion-ambiental/participacion-publica/anexos/sgresiduos/envases/Texto%20consulta%20p%C3%BAblica%20previa%20nuevo%20RDERE.pdf',
        domain: 'miteco.gob.es',
        label: 'MITECO · Consulta previa',
    },
    hosteleria: {
        href: 'https://www.hosteleriadigital.es/2026/09/17/la-singularidad-de-la-hosteleria-exige-su-excepcion-del-sddr-en-el-consumo-en-local/',
        domain: 'hosteleriadigital.es',
        label: 'Hostelería Digital',
    },
    foodretail: {
        href: 'https://www.foodretail.es/sector-fmcg/jose-maria-bonmati-presidente-de-sddr-para-espana-hemos-hecho-los-deberes-del-real-decreto-pero-seamos-realistas-tener-un-sistema-operativo-en-2026-es-imposible.html',
        domain: 'foodretail.es',
        label: 'Food Retail',
    },
    anuario: {
        href: 'https://hosteleriadeespana.es/publicaciones-hosteleria.html',
        domain: 'hosteleriadeespana.es',
        label: 'Anuario Hostelería de España',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

export function SddrHosteleriaArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'repartidor', label: t(k('toc.repartidor'), 'Te lo contó el repartidor, entre cajas') },
        { id: 'que-es', label: t(k('toc.queEs'), 'Qué es el SDDR y de dónde sale la fecha') },
        { id: 'albaran', label: t(k('toc.albaran'), 'La pega llega en el albarán, no en la barra') },
        { id: 'devolver', label: t(k('toc.devolver'), '¿Tengo que devolver dinero por una lata vacía?') },
        { id: 'sala', label: t(k('toc.sala'), '¿Se cobra si el cliente consume en el local?') },
        { id: 'coste', label: t(k('toc.coste'), 'Lo que cuesta cada envase que no vuelve') },
        { id: 'vidrio', label: t(k('toc.vidrio'), 'Ojo con el dato de los 23 envases') },
        { id: 'ahora', label: t(k('toc.ahora'), 'Qué hacer ahora') },
        { id: 'pendientes', label: t(k('toc.pendientes'), 'Lo que hoy no se sabe') },
    ]

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo del SDDR en hostelería y me gustaría contar cuántos envases de depósito muevo de verdad.',
        ),
    )

    const weekActions: ReactNode[] = [
        <>
            <strong>{t(k('weekActions.1.strong'), 'Cuenta cuántos envases SDDR mueves de verdad.')}</strong>{' '}
            {t(
                k('weekActions.1.after'),
                'Latas, botellines de plástico y briks, separados del vidrio. Sin ese número no puedes valorar nada. Es lo que más tiempo lleva y lo único que no depende de la norma.',
            )}
        </>,
        <>
            <strong>
                {t(k('weekActions.2.strong'), 'Pregunta a tu distribuidor cómo va a facturarte el depósito')}
            </strong>{' '}
            {t(
                k('weekActions.2.after'),
                'y si va a recoger vacíos. Esa respuesta decide si esto te cuesta cero o te cuesta 1.750 euros.',
            )}
        </>,
        <>
            <strong>
                {t(
                    k('weekActions.3.strong'),
                    'Separa el delivery y el para llevar del consumo en sala en tu sistema de ventas.',
                )}
            </strong>{' '}
            {t(
                k('weekActions.3.after'),
                'En todas las propuestas sobre la mesa, esos canales van por caminos distintos.',
            )}
        </>,
        <>
            <strong>{t(k('weekActions.4.strong'), 'No compres nada.')}</strong>{' '}
            {t(k('weekActions.4.after'), 'Ninguna máquina, ningún servicio. No hay especificación técnica publicada.')}
        </>,
    ]

    const sources = [
        {
            label: t(
                k('sources.1'),
                'Real Decreto 1055/2022, de 27 de diciembre, de envases y residuos de envases, artículos 46 a 48',
            ),
            href: REF.boe.href,
        },
        {
            label: t(
                k('sources.2'),
                'Informe del MITECO sobre recogida separada de botellas de plástico de un solo uso, año 2023, publicado el 26 de noviembre de 2024',
            ),
            href: REF.miteco.href,
        },
        {
            label: t(
                k('sources.3'),
                'Consulta pública previa del MITECO sobre el nuevo real decreto de envases y residuos de envases',
            ),
            href: REF.consulta.href,
        },
        {
            label: t(
                k('sources.4'),
                'Hostelería de España, propuesta de excepción del consumo en local, 17 de septiembre de 2026, recogida por Hostelería Digital',
            ),
            href: REF.hosteleria.href,
        },
        {
            label: t(
                k('sources.5'),
                'Declaraciones de José María Bonmatí, presidente de SDDR para España, julio de 2025',
            ),
            href: REF.foodretail.href,
        },
        {
            label: t(k('sources.6'), 'Verificación de fechas y desmentidos de prensa: Maldita.es, 6 de mayo de 2026'),
            href: 'https://maldita.es/desinfo/20260506/noviembre-2026-nuevo-sistema-retorno-envases-recargo-aplicaria/',
        },
        {
            label: t(k('sources.7'), 'Anuario de Hostelería de España, más de 300.000 establecimientos'),
            href: REF.anuario.href,
        },
    ]

    const ownSource = t(k('sources.8'), 'Cuenta por establecimiento y reparto sectorial: elaboración propia de Tipi')

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(k('dek.beforeDate'), 'El SDDR obligatorio tiene fecha límite legal: el ')}
                            <strong>{t(k('dek.date'), '26 de noviembre de 2026')}</strong>
                            {t(k('dek.afterDate'), '. Se activó porque España recogió por separado el ')}
                            <strong>{t(k('dek.collected'), '41,3 %')}</strong>
                            {t(
                                k('dek.afterCollected'),
                                ' de las botellas de plástico de un solo uso en 2023, cuando la ley exigía el ',
                            )}
                            <strong>{t(k('dek.target'), '70 %')}</strong>
                            {t(
                                k('dek.afterTarget'),
                                '. El depósito será de 10 céntimos o más por envase, y afecta a botellas de plástico de hasta 3 litros, latas y briks de bebidas. El vidrio queda fuera. Y el depósito lo adelantas tú, en el albarán del proveedor, antes de que ningún cliente pase por la puerta.',
                            )}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Albarán de proveedor junto a latas y botellines de plástico en la trastienda de un bar',
                    )}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <ArticleReadingLayout
                    intro={
                        <div className={article.prose}>
                            <h2 id='repartidor' className={cn(article.h2, 'mt-0')}>
                                {t(k('h2.repartidor'), 'Te lo contó el repartidor, entre cajas')}
                            </h2>
                            <p className={article.p}>
                                {t(
                                    k('repartidor.p1'),
                                    'Descargando el pedido de refrescos, el de la furgoneta te suelta que en noviembre las latas suben diez céntimos.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('repartidor.p2'),
                                    'Que es un depósito, que se devuelve, que ya te contarán. Y se va, porque tiene doce paradas más.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('repartidor.p3'),
                                    'Lo que te acaba de contar es verdad a medias. La otra mitad no la ha escrito nadie todavía.',
                                )}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <section id='que-es'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.queEs'), 'Qué es el SDDR y de dónde sale la fecha')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(
                                    k('queEs.p1'),
                                    'El SDDR, sistema de depósito, devolución y retorno, es un importe reembolsable que viaja con cada envase de bebida: se cobra al venderlo y se devuelve al retornarlo vacío. No es un impuesto.',
                                )}
                            </p>
                        </div>

                        <div className={article.tableWrap}>
                            <table className={article.tableFacts}>
                                <thead>
                                    <tr>
                                        <th scope='col'>{t(k('table.col.what'), 'Qué')}</th>
                                        <th scope='col'>{t(k('table.col.data'), 'Dato')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <th scope='row'>{t(k('table.date.label'), 'Fecha límite legal')}</th>
                                        <td>
                                            <strong>{t(k('table.date.value'), '26 de noviembre de 2026')}</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>{t(k('table.deposit.label'), 'Depósito por envase')}</th>
                                        <td>
                                            <strong>{t(k('table.deposit.value'), '10 céntimos o más')}</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>{t(k('table.included.label'), 'Envases incluidos')}</th>
                                        <td>
                                            {t(
                                                k('table.included.value'),
                                                'Botellas de plástico hasta 3 L, latas y briks',
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>{t(k('table.excluded.label'), 'Envases excluidos')}</th>
                                        <td>
                                            <strong>{t(k('table.excluded.value.glass'), 'El vidrio')}</strong>
                                            {t(k('table.excluded.value.rest'), ', y leche, vino y espirituosos')}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className={article.prose}>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(k('queEs.p2.before'), 'El ')}
                                    <a
                                        href={REF.boe.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('queEs.p2.link'), 'artículo 47 del Real Decreto 1055/2022')}
                                    </a>
                                    <SourceChip source='boe' />
                                </span>
                                {t(
                                    k('queEs.p2.after'),
                                    ' obliga a implantar el SDDR en todo el territorio, en el plazo de dos años, si España incumple los objetivos de recogida separada de botellas de plástico de un solo uso.',
                                )}
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(k('queEs.p3.before'), 'España recogió el ')}
                                    <strong>{t(k('queEs.p3.pct'), '41,3 %')}</strong>
                                    {t(
                                        k('queEs.p3.mid'),
                                        ' de esas botellas en 2023, frente al objetivo del 70 %. El ',
                                    )}
                                    <a
                                        href={REF.miteco.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(
                                            k('queEs.p3.link'),
                                            'informe del MITECO con ese dato se publicó el 26 de noviembre de 2024',
                                        )}
                                    </a>
                                    <SourceChip source='miteco' />
                                </span>
                                {t(k('queEs.p3.after'), '. De ahí salen los dos años.')}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(k('queEs.p4.before'), 'Sobre el importe, el real decreto dice que el depósito ')}
                                <em>
                                    {t(
                                        k('queEs.p4.quote'),
                                        '"será fijado libremente por los sistemas de responsabilidad ampliada del productor en cuantía igual o superior a los 10 céntimos de euros"',
                                    )}
                                </em>
                                {t(k('queEs.p4.after'), '. Diez es el suelo, no el precio.')}
                            </p>
                        </div>
                    </section>

                    <section id='albaran'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.albaran'), 'La pega llega en el albarán, no en la barra')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('albaran.p1'),
                                        'El depósito lo pagas tú primero, al comprarle la mercancía a tu proveedor.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('albaran.p1.after'),
                                    'El real decreto carga el primer pago sobre el productor, y ese importe baja por la cadena hasta tu factura.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('albaran.p2'),
                                    'Pagas los diez céntimos en octubre y los recuperas cuando el envase vuelva al sistema. Si vuelve.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('albaran.p3'),
                                    'Si el vacío acaba en el contenedor amarillo, como hoy, esos diez céntimos dejan de ser caja parada y pasan a ser coste.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='devolver'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.devolver'), '¿Tengo que devolver dinero a quien me traiga una lata vacía?')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(k('devolver.p1.before'), 'Hoy, a 17 de septiembre de 2026, ')}
                                <strong>
                                    {t(k('devolver.p1.strong'), 'no hay ninguna norma publicada que te obligue a eso')}
                                </strong>
                                {t(k('devolver.p1.after'), '. Tampoco ninguna que te libre.')}
                            </p>
                            <p className={article.p}>
                                {t(k('devolver.p2.before'), 'El artículo 47 solo dice que ')}
                                <em>
                                    {t(
                                        k('devolver.p2.quote'),
                                        '"los comerciantes o distribuidores de bebidas envasadas deberán participar en los sistemas de depósito, devolución y retorno que se establezcan"',
                                    )}
                                </em>
                                {t(k('devolver.p2.after'), '.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('devolver.p3'),
                                    'Participar. Ni una palabra sobre qué significa eso si lo tuyo es una barra.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('devolver.p4'),
                                    'Sigue sin decidirse si un bar es punto de retorno de envases que no ha vendido él, si habrá umbral de superficie para los locales pequeños, cómo recuperas el depósito que adelantaste y qué compensación cobra quien acepte devoluciones.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='sala'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.sala'), '¿Se cobra el depósito si el cliente consume en el local?')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(k('sala.p1'), 'Es la pelea de esta semana y no tiene ganador.')}
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    <strong>
                                        {t(
                                            k('sala.p2.strong'),
                                            'El 17 de septiembre de 2026, Hostelería de España pidió que el consumo en sala quede exceptuado del SDDR.',
                                        )}
                                    </strong>
                                    <SourceChip source='hosteleria' />
                                </span>{' '}
                                {t(k('sala.p2.before'), 'Su argumento: ')}
                                <em>
                                    {t(
                                        k('sala.p2.quote'),
                                        '"el envase se abre y se consume en el local, permaneciendo en todo momento bajo el control del operador hostelero"',
                                    )}
                                </em>
                                {t(k('sala.p2.after'), '.')}
                            </p>
                            <p className={article.p}>
                                {t(k('sala.p3'), 'Delivery y para llevar irían por el régimen general.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('sala.p4'),
                                    'Para el retorno propone usar la red que ya existe: que el distribuidor que te trae el pedido se lleve los vacíos. Y crear "puntos de retorno social" en pueblos, voluntarios y compensados.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>{t(k('sala.p5'), 'Es una petición. No es la norma.')}</strong>
                            </p>
                        </div>
                    </section>

                    <section id='coste'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.coste'), 'Lo que cuesta cada envase que no vuelve')}
                        </h2>
                        <div className={article.prose}>
                            <p className={cn(article.p, 'text-article-muted')}>
                                <em>
                                    {t(
                                        k('coste.disclaimer'),
                                        'Estimación propia sobre un local que venda 50 latas, botellines de plástico y briks al día y abra 350 días al año. No es un dato de mercado.',
                                    )}
                                </em>
                            </p>
                            <p className={article.p}>
                                {t(k('coste.p1.before'), '50 envases × 0,10 € = ')}
                                <strong>{t(k('coste.p1.day'), '5 euros al día')}</strong>
                                {t(k('coste.p1.mid'), ', ')}
                                <strong>{t(k('coste.p1.year'), '1.750 euros al año')}</strong>
                                {t(k('coste.p1.after'), ' adelantados en depósitos.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('coste.p2'),
                                    'Si vuelven todos, tu coste es cero y solo has tenido unos 100 euros parados en el almacén. Manejable.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(k('coste.p3'), 'Si no vuelve ninguno, son 1.750 euros de coste nuevo.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('coste.p4'),
                                    'Contra el margen real del que hablan hoy las asociaciones del sector, entre el 3 % y el 4 %, un local que factura 300.000 euros gana entre 9.000 y 12.000 al año.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('coste.p5'),
                                        'Esos 1.750 euros son entre el 15 % y el 19 % del beneficio de todo el año.',
                                    )}
                                </strong>{' '}
                                {t(k('coste.p5.after'), 'Por un trámite, no por vender menos.')}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <span className={article.cite}>
                                    {t(k('coste.p6.before'), 'A escala de sector, 1.750 euros por cada uno de los ')}
                                    <strong>{t(k('coste.p6.venues'), 'más de 300.000 establecimientos')}</strong>
                                    {t(k('coste.p6.mid'), ' del Anuario de Hostelería de España')}
                                    <SourceChip source='anuario' />
                                </span>
                                {t(k('coste.p6.after'), ' son unos ')}
                                <strong>{t(k('coste.p6.total'), '525 millones de euros al año')}</strong>
                                {t(k('coste.p6.end'), ' circulando en depósitos por la hostelería. ')}
                                <em>
                                    {t(
                                        k('coste.p6.note'),
                                        'Reparto lineal propio: sirve para dimensionar, no para presupuestar.',
                                    )}
                                </em>
                            </p>
                        </div>
                    </section>

                    <section id='vidrio'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.vidrio'), 'Ojo con el dato de los 23 envases')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(
                                    k('vidrio.p1.before'),
                                    'Circula mucho estos días: un establecimiento hostelero genera de media ',
                                )}
                                <strong>{t(k('vidrio.p1.strong'), '23 envases de vidrio al día')}</strong>
                                {t(k('vidrio.p1.after'), ', frente a 0,5 de un hogar.')}
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(k('vidrio.p2.before'), 'Es un dato real y es de Hostelería de España. ')}
                                    <strong>
                                        {t(
                                            k('vidrio.p2.strong'),
                                            'Pero es de vidrio, y el vidrio no entra en el SDDR obligatorio.',
                                        )}
                                    </strong>
                                    <SourceChip source='hosteleria' />
                                </span>
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('vidrio.p3'),
                                    'Tus botellas de cerveza y de vino no llevan depósito. Tus latas y tus botellines de agua, sí. Si alguien te hace una cuenta mezclando las dos cosas, la cuenta está mal.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='ahora'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.ahora'), 'Qué hacer ahora, empezando por lo que más tarda')}
                        </h2>
                        <div className={article.prose}>
                            <NewsArticleNumList items={weekActions} />
                        </div>
                    </section>

                    <section id='pendientes'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.pendientes'), 'Lo que hoy no se sabe, y quién dice qué')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    <strong>
                                        {t(k('pendientes.p1.strong'), 'El desarrollo normativo no está publicado.')}
                                    </strong>{' '}
                                    {t(k('pendientes.p1.before'), 'El MITECO abrió una ')}
                                    <a
                                        href={REF.consulta.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(
                                            k('pendientes.p1.link'),
                                            'consulta pública previa sobre el nuevo real decreto de envases',
                                        )}
                                    </a>
                                    <SourceChip source='consulta' />
                                </span>
                                {t(
                                    k('pendientes.p1.after'),
                                    ' y anunció una participación posterior con el borrador. Ese borrador es el que tiene que contestar todo lo anterior.',
                                )}
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    <strong>
                                        {t(k('pendientes.p2.strong'), 'El propio operador duda del plazo.')}
                                    </strong>{' '}
                                    {t(
                                        k('pendientes.p2.before'),
                                        'José María Bonmatí, presidente de SDDR para España, la entidad que solicitó operar el sistema, ',
                                    )}
                                    <a
                                        href={REF.foodretail.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('pendientes.p2.link'), 'dijo en julio de 2025')}
                                    </a>
                                    <SourceChip source='foodretail' />
                                </span>
                                {t(k('pendientes.p2.mid'), ' que ')}
                                <em>
                                    {t(
                                        k('pendientes.p2.quote'),
                                        '"tener un sistema de retorno de envases en España para noviembre de 2026 es imposible"',
                                    )}
                                </em>
                                {t(
                                    k('pendientes.p2.after'),
                                    '. Habla de cerca de 500.000 puntos de venta entre comercio y hostelería.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(k('pendientes.p3.strong'), 'Las fechas de prensa no cuadran entre sí.')}
                                </strong>{' '}
                                {t(
                                    k('pendientes.p3.after'),
                                    'Varios medios generalistas han dado el 12 de agosto de 2026 como entrada en vigor y otros sitúan la obligación en 2029. La única fecha sostenida por un documento oficial es el 26 de noviembre de 2026, y es un tope legal, no el día en que algo empieza a funcionar en tu barra.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('pendientes.p4'),
                                    'Así que antes de enfadarte con la norma, cuenta tus latas. Es el único número de toda esta historia que ya puedes tener hoy.',
                                )}
                            </p>
                        </div>
                    </section>

                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(
                                k('closing.p1'),
                                'Si no sabes cuántas latas, botellines y briks mueves al mes, no puedes saber si el SDDR te cuesta cero o te come el margen. Cuéntanoslo y lo miramos contigo.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(k('closing.p2'), 'Seguimos contando lo que a otros no les interesa contar.')}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), '¿Sabes cuántos envases de depósito mueves al mes?')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Te ayudamos a sacar ese número de tus albaranes antes de que el depósito llegue a la factura.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <a
                            href={contactWhatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(article.btn, article.btnSolid)}
                        >
                            {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                        </a>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={article.link}
                                >
                                    {source.label}
                                </a>
                            </li>
                        ))}
                        <li>{ownSource}</li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
